import { useCallback, useEffect, useRef, useState } from 'react';
import { useCapa, useSubpantalla } from '../ui/navegacionReact';
import {
  terminal, ErrorTerminal, credencialGuardada, guardarCredencial,
  aparatoGuardado, olvidar, cartelDeUbicacion, tocaPreguntarDonde,
} from './api';
import {
  ESTADOS, girarLista, girarCantidad, giroConfirmado, leyendaDe, ledDe,
  LEYENDA_LED, girarDigito, etiquetaDeLote, comoEstaLaTerminal,
} from './perilla';
import CodigoQR from '../ui/CodigoQR';
import * as cola from './cola';
import { leyendaDeCola } from './cola';

// TERMINAL VIRTUAL · Anexo B, paso B-0
//
// ESTA PANTALLA **ES** UNA TERMINAL, no una simulación de una. Se enrola
// con los mismos seis dígitos, pide el mismo QR, manda los mismos lotes
// y recibe los mismos errores. No tiene ni una ruta propia: si tuviera
// atajos dejaría de servir para lo que existe, que es probar T-2 a T-4
// antes de que haya un aparato en la mesa.
//
// PARA QUÉ SIRVE, en orden de utilidad real:
//   1. Probar la terminal sin hardware y sin esperar a la UNO Q.
//   2. Enseñarla en la laptop delante de un cliente, sin llevar el
//      aparato en la mochila.
//   3. Una tableta vieja pegada al rack, si el cliente no quiere comprar
//      hardware. Esto la vuelve un producto, no solo una herramienta.
//
// POR QUÉ NO TIENE SESIÓN DE PERSONA. Se bifurca en `main.jsx` ANTES de
// montar ningún proveedor de sesión, igual que la tienda: bajo
// `/terminal-virtual` no existe `ProveedorAuth`, así que esta pantalla no
// puede leer por descuido el token de quien haya entrado a la consola en
// ese mismo navegador. Aquí importa más que en la tienda: si mezclara las
// dos credenciales, el kárdex diría que la repisa hizo lo que hizo el
// encargado — que es exactamente lo que T-1 vino a arreglar.
//
// EL CONTROL ES UNO SOLO, como en el aparato: la rueda del ratón o las
// flechas ↑ ↓ giran, Enter o la barra aprietan, y mantener Enter dos
// segundos es «mantener apretado».

const MS_LATIDO = 30_000;
const MS_REINTENTO = 15_000;
const MS_MIRAR_QR = 2_000;

function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  // Un navegador viejo no deja a la terminal sin poder mandar nada: el
  // id solo tiene que ser único, no criptográfico.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export default function TerminalApp() {
  const [credencial, setCredencial] = useState(() => credencialGuardada());
  const [aparato, setAparato] = useState(() => aparatoGuardado());

  if (!credencial) {
    return (
      <Enrolar
        onEnrolada={(d) => {
          guardarCredencial(d.credencial, {
            nombre: d.nombre, ubicacion: d.ubicacion,
            permite_ajuste_directo: d.permite_ajuste_directo,
          });
          setCredencial(d.credencial);
          setAparato({
            nombre: d.nombre, ubicacion: d.ubicacion,
            permite_ajuste_directo: d.permite_ajuste_directo,
          });
        }}
      />
    );
  }

  return (
    <Pantalla
      aparato={aparato}
      onOlvidar={() => { olvidar(); setCredencial(null); setAparato(null); }}
    />
  );
}

// =====================================================================
// LOS SEIS DÍGITOS
// =====================================================================
function Enrolar({ onEnrolada }) {
  const [digitos, setDigitos] = useState([0, 0, 0, 0, 0, 0]);
  const [cursor, setCursor] = useState(0);
  const [error, setError] = useState(null);
  const [enrolando, setEnrolando] = useState(false);

  const girar = useCallback((pasos) => {
    setDigitos((d) => {
      const copia = [...d];
      copia[cursor] = girarDigito(copia[cursor], pasos);
      return copia;
    });
  }, [cursor]);

  const apretar = useCallback(async () => {
    if (cursor < 5) { setCursor(cursor + 1); return; }
    setError(null);
    setEnrolando(true);
    try {
      onEnrolada(await terminal.enrolar(digitos.join('')));
    } catch (e) {
      setError(e instanceof ErrorTerminal ? e.message : 'No se pudo conectar.');
      setCursor(0);
    } finally {
      setEnrolando(false);
    }
  }, [cursor, digitos, onEnrolada]);

  useTeclado({ girar, apretar });

  return (
    <Marco>
      <h1 className="text-2xl font-bold">Teclea el número de TEGO</h1>
      <p className="text-sm opacity-70 max-w-sm">
        Seis dígitos. <strong>Gira</strong> con la rueda del ratón o las
        flechas ↑ ↓ para elegir cada uno, <strong>aprieta</strong> con Enter
        para pasar al siguiente.
      </p>

      <div className="flex gap-2" role="group" aria-label="Número de seis dígitos">
        {digitos.map((d, i) => (
          <div
            key={i}
            aria-label={`Dígito ${i + 1}: ${d}`}
            className={`w-12 h-16 rounded-lg flex items-center justify-center
              text-3xl font-mono font-bold border-2 ${
                i === cursor
                  ? 'border-green-400 bg-green-950 text-green-200'
                  : 'border-gray-700 bg-gray-900 text-gray-400'}`}
          >
            {d}
          </div>
        ))}
      </div>

      {error && (
        <p role="alert" className="text-red-300 text-sm max-w-sm text-center">{error}</p>
      )}
      {enrolando && <p className="text-sm opacity-70">Conectando…</p>}

      <Perilla onGirar={girar} onApretar={apretar} etiqueta={
        cursor < 5 ? 'Aprieta para el siguiente dígito' : 'Aprieta para conectar'} />

      <p className="text-xs opacity-50 max-w-sm text-center">
        ¿No tienes el número? Lo da quien administra TEGO en
        Administración → Terminales de conteo. Dura 10 minutos.
      </p>
    </Marco>
  );
}

// =====================================================================
// LA TERMINAL, YA CONECTADA
// =====================================================================
function Pantalla({ aparato, onOlvidar }) {
  const [lista, setLista] = useState(null);
  const [indice, setIndice] = useState(0);
  const [editando, setEditando] = useState(null);
  const [qr, setQr] = useState(null);
  const [motivos, setMotivos] = useState([]);
  const [rechazando, setRechazando] = useState(null);
  // TC-30 · EL PASO DEL LOTE. Nulo salvo mientras se elige, que es
  // exactamente cuando la perilla deja de mover productos y mueve lotes.
  // Lleva DENTRO el giro ya calculado: se confirma lo que se vio, no se
  // vuelve a restar por si acaso.
  const [eligiendoLote, setEligiendoLote] = useState(null);
  // TC-31 · EL MENÚ DEL SUPERVISOR. Nulo mientras no se abre. Lo que
  // dice si se puede abrir lo contesta el SERVIDOR, no este estado.
  const [supervisor, setSupervisor] = useState(null);
  const [menuAbierto, setMenuAbierto] = useState(false);
  // ATRÁS EN LA TERMINAL · TC-DR-AT. El menú de supervisor es una capa y
  // la cantidad que se está ajustando una subpantalla: atrás cierra el
  // menú o deja el ajuste sin cambios, igual que «Volver a la repisa».
  // `indice` no ocupa entrada: es por dónde va la perilla en la lista, no
  // una vista (cada giro sería un atrás). Desde la repisa, atrás sale.
  useCapa(menuAbierto, () => setMenuAbierto(false));
  useSubpantalla('editando', editando !== null, () => setEditando(null), { reemplaza: true });
  const [misRechazos, setMisRechazos] = useState(0);
  const [aviso, setAviso] = useState(null);
  // II-4 · «Dónde acomodarlo», tras un giro que metió mercancía.
  const [cartel, setCartel] = useState(null);
  const [error, setError] = useState(null);
  const [pendientes, setPendientes] = useState(() => cola.leer());
  const [ultimoContacto, setUltimoContacto] = useState(null);
  const nonceActivo = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const d = await terminal.miLista();
      setLista(d);
      setUltimoContacto(Date.now());
      setError(null);
      return d;
    } catch (e) {
      setError(e instanceof ErrorTerminal ? e.message : 'Sin conexión con TEGO.');
      return null;
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // SE PREGUNTA AL SERVIDOR SI QUIEN ESTÁ DELANTE ES SUPERVISOR, y se
  // vuelve a preguntar cada vez que cambia quién está vinculado: el
  // menú de un encargado no puede quedarse en pantalla cuando entra
  // otra persona.
  //
  // UN FALLO AQUÍ NO ROMPE LA TERMINAL: si no se puede preguntar, no
  // hay menú y todo lo demás sigue. Es un añadido, no una condición.
  useEffect(() => {
    if (!lista || lista.modo_vitrina !== false) {
      setSupervisor(null);
      setMenuAbierto(false);
      return;
    }
    terminal.supervisor().then(setSupervisor).catch(() => setSupervisor(null));
    // LAS DOS CLAVES Y NO `lista` ENTERA, a propósito: `lista` se
    // reemplaza en cada recarga —cada giro confirmado la recarga— y
    // depender de ella pediría el menú del supervisor decenas de veces
    // por turno para recibir siempre lo mismo. Lo que de verdad cambia
    // el menú es quién está vinculado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lista?.modo_vitrina, lista?.operador]);

  // LOS MOTIVOS SE BAJAN UNA VEZ Y SE GUARDAN. Sin red hay que poder
  // seguir rechazando, y un motivo que no se puede elegir es un rechazo
  // que no se apunta.
  useEffect(() => {
    terminal.motivos().then(setMotivos).catch(() => {});
  }, []);

  // EL LATIDO. Lo mismo que hará el aparato físico: decir que sigue ahí
  // y cuántos giros lleva sin subir. Sin esto, «Este servidor» no puede
  // distinguir una terminal apagada de una que nunca existió.
  useEffect(() => {
    const t = setInterval(() => {
      terminal.latido({ version: 'virtual-1', pendientes: pendientes.length })
        .then(() => setUltimoContacto(Date.now()))
        .catch(() => {});
    }, MS_LATIDO);
    return () => clearInterval(t);
  }, [pendientes.length]);

  // REINTENTAR LA COLA SOLA, sin que nadie pulse nada. Un aparato que
  // exige que alguien pulse «reintentar» para no perder trabajo es un
  // aparato que pierde trabajo: quien giró la perilla se fue hace rato.
  const vaciarCola = useCallback(async () => {
    if (cola.leer().length === 0) return;
    const r = await cola.subir((giros) => terminal.mover(giros));
    setPendientes(cola.leer());
    if (r.error) return;
    setUltimoContacto(Date.now());
    if (r.subidos > 0) {
      setAviso(`Se subieron ${r.subidos} giro(s) que estaban pendientes.`);
      cargar();
    }
    if (r.rechazados.length > 0) {
      // NO SE ESCONDE UN RECHAZO. El servidor dice por qué y qué hacer;
      // tragárselo dejaría al operador creyendo que su conteo entró.
      setError(`${r.rechazados.length} giro(s) no se pudieron guardar: `
        + (r.rechazados[0].detalle || 'sin detalle'));
    }
  }, [cargar]);

  useEffect(() => {
    const t = setInterval(vaciarCola, MS_REINTENTO);
    // Y al volver la red, sin esperar al reloj.
    window.addEventListener('online', vaciarCola);
    return () => { clearInterval(t); window.removeEventListener('online', vaciarCola); };
  }, [vaciarCola]);

  // PREGUNTAR POR EL QR CADA DOS SEGUNDOS, igual que el aparato. Es la
  // ruta más llamada de todo el sistema y por eso se midió aparte:
  // 0.1 ms de mediana con cuarenta terminales.
  useEffect(() => {
    if (!qr) return undefined;
    nonceActivo.current = qr.nonce;
    const t = setInterval(async () => {
      try {
        const e = await terminal.mirarQR(qr.nonce);
        setUltimoContacto(Date.now());
        if (e.vinculado) {
          setQr(null);
          setAviso(`Hola, ${e.operador}`);
          cargar();
        } else if (e.caducado) {
          // No se le pide nada a nadie: la terminal pinta otro sola.
          pedirQR();
        }
      } catch { /* la siguiente vuelta lo intenta otra vez */ }
    }, MS_MIRAR_QR);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr]);

  async function pedirQR() {
    setError(null);
    try {
      setQr(await terminal.pedirQR());
      setUltimoContacto(Date.now());
    } catch (e) {
      setError(e instanceof ErrorTerminal ? e.message : 'No se pudo pedir el código.');
    }
  }

  const productos = lista?.productos || [];
  const producto = productos[indice] || null;
  const vitrina = lista?.modo_vitrina !== false;

  const girar = useCallback((pasos) => {
    if (eligiendoLote !== null) {
      setEligiendoLote((e) => (e && e.lotes.length
        ? { ...e, indice: girarLista(e.indice, pasos, e.lotes.length) }
        : e));
    } else if (rechazando !== null) {
      setRechazando((i) => girarLista(i, pasos, motivos.length));
    } else if (editando !== null) {
      setEditando((c) => girarCantidad(c, pasos));
    } else {
      setIndice((i) => girarLista(i, pasos, productos.length));
    }
  }, [editando, rechazando, eligiendoLote, motivos.length, productos.length]);

  // MANDAR EL GIRO, QUE AHORA TIENE DOS CAMINOS · TC-30. Uno directo,
  // el de siempre; y otro que pasa antes por elegir lote. El envío es el
  // mismo en los dos —incluida la cola sin red— porque duplicarlo sería
  // la forma más segura de que un día solo uno de los dos encole.
  const mandarGiro = useCallback(async (giro, sku, idProducto) => {
    setError(null);
    setCartel(null);
    try {
      const r = await terminal.mover([giro]);
      setUltimoContacto(Date.now());
      const uno = r.resultados?.[0];
      if (uno && !uno.aplicado) {
        setError(uno.detalle || 'No se pudo aplicar.');
      } else {
        setAviso(`${sku}: ${giro.delta > 0 ? '+' : ''}${giro.delta}`
          + (giro.codigo_lote ? ` · lote ${giro.codigo_lote}` : ''));
        cargar();
        // II-4 · SI ENTRÓ MERCANCÍA, «DÓNDE ACOMODARLO». Es un añadido:
        // si no contesta, la terminal sigue igual y no se dice nada.
        if (tocaPreguntarDonde(giro, { id_producto: idProducto })) {
          terminal.dondeAcomodar(idProducto, giro.delta)
            .then((d) => setCartel(cartelDeUbicacion(d)))
            .catch(() => {});
        }
      }
    } catch (e) {
      // SIN RED NO SE PIERDE EL GIRO: se encola con su hora y se dice en
      // pantalla. Un aparato que finge que todo está bien es peor que
      // uno que avisa. Y el lote elegido viaja DENTRO del giro, así que
      // la cola lo conserva sin saber que existe.
      //
      // UN 409 NO SE ENCOLA. «Modo vitrina» o «ajuste directo apagado»
      // no se arreglan reintentando: encolarlos dejaría a la terminal
      // reintentando para siempre algo que nunca va a entrar.
      if (e?.codigo && e.codigo >= 400 && e.codigo < 500) {
        setError(e.message);
      } else {
        setPendientes(cola.encolar(giro));
        setError('Sin conexión con TEGO. El giro queda apuntado y se '
          + 'subirá solo en cuanto vuelva la red.');
      }
    }
  }, [cargar]);

  const apretar = useCallback(async () => {
    // EL LOTE PRIMERO DE TODO · TC-30. Si se está eligiendo lote,
    // apretar confirma ése y manda el giro que ya venía calculado.
    if (eligiendoLote !== null) {
      const elegido = eligiendoLote.lotes[eligiendoLote.indice];
      const { giro, sku, idProducto } = eligiendoLote;
      setEligiendoLote(null);
      if (!elegido) return;
      await mandarGiro({ ...giro, codigo_lote: elegido.codigo_lote }, sku, idProducto);
      return;
    }
    // EL BOTÓN ROJO DESPUÉS. Si se está eligiendo motivo, apretar
    // confirma el rechazo y no hace ninguna otra cosa.
    if (rechazando !== null) {
      const motivo = motivos[rechazando];
      setRechazando(null);
      if (!motivo) return;
      setError(null);
      try {
        const r = await terminal.rechazar([{
          id_operacion: uuid(),
          sku: producto?.sku,
          motivo: motivo.clave,
          cantidad: 1,
        }]);
        setUltimoContacto(Date.now());
        const uno = r.resultados?.[0];
        if (uno && !uno.aplicado) {
          setError(uno.detalle || 'No se pudo apuntar.');
        } else {
          setMisRechazos(r.mis_rechazos_hoy || 0);
          setAviso(`Apuntado: ${motivo.nombre}.`);
        }
      } catch (e) {
        setError(e instanceof ErrorTerminal ? e.message
          : 'Sin conexión. Vuelve a intentarlo.');
      }
      return;
    }
    if (!producto) return;
    if (editando === null) {
      setEditando(Number(producto.existencia ?? 0));
      return;
    }
    const giro = giroConfirmado(producto.sku, editando, producto.existencia, uuid());
    setEditando(null);
    if (!giro) { setAviso('Sin cambios.'); return; }

    // ================================================================
    // SI EL PRODUCTO MANEJA LOTES, SE PREGUNTA CUÁL · TC-30 (§A3)
    // ================================================================
    // SE PREGUNTA ANTES DE MANDAR, y no después de que el servidor diga
    // que no. Un error que llega DESPUÉS del giro, en una pantalla de
    // dos líneas, deja al operador con un número confirmado y nada que
    // hacer con él. Es la regla de prevenir en vez de regañar.
    if (producto.maneja_lotes) {
      setError(null);
      try {
        const d = await terminal.lotes(producto.sku);
        setUltimoContacto(Date.now());
        if (!d.lotes.length) {
          // UN CAMINO SIN SALIDA SE DICE, con qué hacer. Encolarlo sería
          // peor: nadie va a poder elegir el lote media hora después.
          setError('Este producto se lleva por lotes y en esta repisa no '
            + 'hay ninguno con existencia. Pide que lo repongan desde '
            + 'TEGO y vuelve a intentarlo.');
          return;
        }
        setEligiendoLote({
          giro, sku: producto.sku, idProducto: producto.id_producto, lotes: d.lotes, indice: 0,
        });
      } catch (e) {
        // SIN RED NO SE PUEDE ELEGIR LOTE, y encolar a ciegas sería
        // inventar la trazabilidad que este paso existe para guardar.
        setError(e instanceof ErrorTerminal ? e.message
          : 'Sin conexión, y este producto se lleva por lotes: hay que '
            + 'elegir cuál. Espera a que vuelva la red.');
      }
      return;
    }

    await mandarGiro(giro, producto.sku, producto.id_producto);
    // `cargar` ya no va aquí: recargar la lista es cosa de `mandarGiro`
    // desde TC-30, y dejarlo en la lista lo volvía a crear sin motivo.
  }, [producto, editando, rechazando, eligiendoLote, motivos, mandarGiro]);

  useTeclado({ girar, apretar, onMantener: pedirQR });

  const led = ledDe({
    pendientes: pendientes.length, ultimoContacto, vinculado: !vitrina,
  });

  return (
    <Marco>
      <Cabecera aparato={aparato} lista={lista} led={led}
                cola={pendientes.length} />

      {qr && <ElQR qr={qr} />}

      {!qr && vitrina && (
        <Vitrina productos={productos} onEscanear={pedirQR} />
      )}

      {!qr && !vitrina && menuAbierto && (
        <MenuSupervisor
          datos={supervisor}
          pendientes={pendientes.length}
          onSincronizar={vaciarCola}
          onSalir={() => setMenuAbierto(false)}
          onCerrar={async (t) => {
            setError(null);
            try {
              const r = await terminal.cerrarSesionAjena(t.id_sesion);
              setAviso(r.mensaje);
              setSupervisor(await terminal.supervisor());
            } catch (e) {
              setError(e instanceof ErrorTerminal ? e.message
                : 'No se pudo cerrar. Vuelve a intentarlo.');
            }
          }}
        />
      )}

      {!qr && !vitrina && !menuAbierto && eligiendoLote !== null && (
        <ElegirLote
          lotes={eligiendoLote.lotes}
          indice={eligiendoLote.indice}
          sku={eligiendoLote.sku}
          delta={eligiendoLote.giro.delta}
        />
      )}

      {!qr && !vitrina && !menuAbierto && eligiendoLote === null && rechazando !== null && (
        <ElegirMotivo
          motivos={motivos}
          indice={rechazando}
          producto={producto}
          mios={misRechazos}
        />
      )}

      {!qr && !vitrina && !menuAbierto && rechazando === null && eligiendoLote === null && (
        <ElProducto
          producto={producto}
          editando={editando}
          aCiegas={lista?.conteo_a_ciegas}
          operador={lista?.operador}
          posicion={productos.length ? `${indice + 1} de ${productos.length}` : ''}
        />
      )}

      {aviso && <p role="status" className="text-green-300 text-lg font-semibold">{aviso}</p>}
      {!qr && !vitrina && <CartelUbicacion cartel={cartel} />}
      {error && <p role="alert" className="text-amber-300 text-sm max-w-md text-center">{error}</p>}

      <Perilla
        onGirar={girar}
        onApretar={apretar}
        onMantener={pedirQR}
        etiqueta={
          qr ? 'Escanea el código con tu teléfono'
            : vitrina ? 'Mantén apretado para pedir el código'
              : eligiendoLote !== null ? 'Gira para elegir el lote · aprieta para confirmar'
                : rechazando !== null ? 'Gira para elegir el motivo · aprieta para apuntarlo'
                  : editando !== null ? 'Aprieta para confirmar'
                    : 'Gira para elegir · aprieta para cambiar'}
      />

      {/* EL BOTÓN ROJO, que en el aparato es un botón físico grande y
          aquí es el suyo. Va aparte de la perilla a propósito: en una
          estación de línea se aprieta con una sola mano y sin mirar. */}
      {!qr && !vitrina && !menuAbierto && eligiendoLote === null && (
        <button
          type="button"
          onClick={() => setRechazando(rechazando === null ? 0 : null)}
          disabled={motivos.length === 0}
          className="bg-red-700 disabled:bg-gray-700 text-white rounded-lg
                     px-5 py-3 font-semibold"
        >
          {rechazando === null ? 'Pieza mala' : 'Cancelar'}
        </button>
      )}
      {/* UN BOTÓN APAGADO DICE POR QUÉ. */}
      {!qr && !vitrina && motivos.length === 0 && (
        <p className="text-xs opacity-60 max-w-xs text-center">
          No se pudo bajar la lista de motivos, así que no se puede apuntar
          una pieza mala todavía. Se reintenta al recargar.
        </p>
      )}

      {/* SOLO SI EL SERVIDOR DIJO QUE SÍ · TC-31. Enseñar el botón a
          quien no puede usarlo y dejar que el servidor lo rechace es
          cómo se hace una pantalla que frustra: en un pasillo, un botón
          que existe es un botón que se aprieta. */}
      {!qr && !vitrina && supervisor?.soy_supervisor && !menuAbierto && (
        <button
          type="button"
          onClick={() => setMenuAbierto(true)}
          className="text-sm underline opacity-70"
        >
          Menú de supervisor
        </button>
      )}

      <button
        type="button"
        onClick={onOlvidar}
        className="text-xs opacity-40 underline"
      >
        Olvidar esta terminal
      </button>
    </Marco>
  );
}

// =====================================================================
// LAS PIEZAS
// =====================================================================
// II-4 · EL CARTEL DE «DÓNDE ACOMODARLO». Dónde y por qué, en letra
// grande; sin sugerencia útil no pinta nada. La sugerencia queda
// pendiente en TEGO: aceptarla o rechazarla es de quien tenga la sesión.
export function CartelUbicacion({ cartel }) {
  if (!cartel) return null;
  return (
    <div role="status" className="border border-sky-400 rounded-lg px-4 py-2 max-w-md text-center">
      <p className="text-xs uppercase opacity-70">Dónde acomodarlo</p>
      {cartel.donde && (
        <p className="text-2xl font-bold text-sky-200">
          {cartel.donde}{cartel.nombre ? ` · ${cartel.nombre}` : ''}
        </p>
      )}
      {cartel.porque && <p className="text-sm opacity-80">{cartel.porque}</p>}
    </div>
  );
}

export function Marco({ children }) {
  return (
    <div className="min-h-dvh bg-gray-950 text-gray-100 flex flex-col
                    items-center justify-center gap-5 p-6 select-none">
      {children}
    </div>
  );
}

export function Cabecera({ aparato, lista, led, cola }) {
  const COLOR = { verde: 'bg-green-500', ambar: 'bg-amber-500', rojo: 'bg-red-500', azul: 'bg-blue-500' };
  return (
    <div className="w-full max-w-md flex items-center gap-3 text-sm">
      <span
        className={`h-3 w-3 rounded-full shrink-0 ${COLOR[led] || COLOR.rojo}`}
        aria-hidden="true"
      />
      {/* EL LED NUNCA VA SOLO: su significado va escrito al lado. Un
          punto de color es exactamente lo que nadie sabe interpretar
          sin manual. */}
      <span className="opacity-70">{LEYENDA_LED[led]}</span>
      <span className="ml-auto opacity-70 truncate">
        {aparato?.nombre}{lista?.ubicacion ? ` · ${lista.ubicacion}` : ''}
      </span>
      {cola > 0 && (
        <span className="text-amber-300 font-semibold shrink-0">
          {leyendaDeCola(cola)}
        </span>
      )}
    </div>
  );
}

export function Vitrina({ productos, onEscanear }) {
  return (
    <div className="text-center space-y-3">
      <h1 className="text-3xl font-bold">Escanea para empezar</h1>
      <p className="opacity-70 max-w-sm">
        Esta terminal enseña lo que hay en la repisa, pero no puede mover
        nada hasta que sepa quién eres.
      </p>
      <button
        type="button"
        onClick={onEscanear}
        className="bg-green-600 text-white rounded-lg px-5 py-3 font-semibold"
      >
        Enséñame el código
      </button>
      {/* SIN SESIÓN SE PUEDE MIRAR, que es media razón de que el aparato
          esté colgado ahí. */}
      <div className="pt-3 text-left max-w-sm mx-auto space-y-1">
        <div className="text-xs uppercase tracking-wide opacity-40">En esta repisa</div>
        {productos.slice(0, 6).map((p) => (
          <div key={p.sku} className="flex justify-between text-sm opacity-80">
            <span className="truncate">{p.nombre}</span>
            <span className="tabular-nums shrink-0 pl-3">
              {p.existencia === null || p.existencia === undefined ? '—' : p.existencia}
            </span>
          </div>
        ))}
        {productos.length === 0 && (
          <div className="text-sm opacity-60">
            Esta repisa está vacía. Mete algo desde TEGO y aparecerá aquí.
          </div>
        )}
      </div>
    </div>
  );
}

export function ElQR({ qr }) {
  // EL QR SE PINTA COMO CUADROS · TC-10 (14 sep 2026). Antes iba el
  // nonce en texto dentro del recuadro: bastaba para probar el recorrido
  // sin hardware, pero no era un QR. Se usa `CodigoQR`, el único sitio
  // de la aplicación que dibuja uno (tinta negra, zona de silencio
  // propia): ninguna dependencia nueva. El texto sigue debajo, para
  // quien copie a mano y para la prueba que lee `data-nonce`.
  return (
    <div className="text-center space-y-3">
      <div className="bg-white p-4 rounded-xl inline-block">
        <CodigoQR valor={qr.nonce} tamano={168} etiqueta="Código para escanear" />
        <div
          data-nonce={qr.nonce}
          className="font-mono text-[10px] break-all text-black max-w-[15rem] mt-2"
        >
          {qr.nonce}
        </div>
      </div>
      {/* QUIEN MIRA EL QR TIENE QUE SABER A QUÉ SE VINCULA: en un
          pasillo hay más de una terminal. */}
      <div className="font-semibold text-lg">{qr.lugar}</div>
      <div className="opacity-70 text-sm">
        Escanéalo con la app de TEGO en tu teléfono. Dura {qr.segundos} segundos
        y la pantalla te dará otro sola.
      </div>
    </div>
  );
}

export function ElProducto({ producto, editando, aCiegas, operador, posicion }) {
  if (!producto) {
    return (
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold">Esta repisa está vacía</h1>
        <p className="opacity-70 max-w-sm">
          No hay nada asignado a esta ubicación todavía. Mete algo desde TEGO
          y aparecerá aquí solo.
        </p>
      </div>
    );
  }
  return (
    <div className="text-center space-y-2 max-w-md">
      {operador && <div className="text-sm text-green-300">Hola, {operador}</div>}
      <div className="text-xs opacity-40 font-mono">{producto.sku} · {posicion}</div>
      <h1 className="text-3xl font-bold leading-tight">{producto.nombre}</h1>
      {editando === null ? (
        <div className="text-xl opacity-80">{leyendaDe(producto, aCiegas)}</div>
      ) : (
        <div className="space-y-1">
          <div className="text-6xl font-bold tabular-nums text-green-300">{editando}</div>
          <div className="text-sm opacity-70">
            Gira para cambiar · aprieta para confirmar
          </div>
        </div>
      )}
    </div>
  );
}

// LA PERILLA EN PANTALLA. Existe para que se pueda usar con el dedo en
// una tableta pegada al rack, no solo con teclado: si solo respondiera a
// las flechas, el caso 3 —«una tableta vieja como terminal»— no
// funcionaría.
export function Perilla({ onGirar, onApretar, onMantener, etiqueta }) {
  const temporizador = useRef(null);
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-3">
        <button type="button" aria-label="Girar a la izquierda"
                onClick={() => onGirar(-1)}
                className="h-14 w-14 rounded-full border-2 border-gray-700 text-2xl">−</button>
        <button
          type="button"
          aria-label="Apretar la perilla"
          onClick={onApretar}
          onPointerDown={() => {
            if (!onMantener) return;
            temporizador.current = setTimeout(onMantener, 2000);
          }}
          onPointerUp={() => clearTimeout(temporizador.current)}
          onPointerLeave={() => clearTimeout(temporizador.current)}
          className="h-20 w-20 rounded-full bg-gray-800 border-4 border-gray-600
                     font-semibold"
        >
          OK
        </button>
        <button type="button" aria-label="Girar a la derecha"
                onClick={() => onGirar(1)}
                className="h-14 w-14 rounded-full border-2 border-gray-700 text-2xl">+</button>
      </div>
      {/* NUNCA UN ICONO SOLO: el control dice lo que hace ahora mismo. */}
      <div className="text-xs opacity-60 text-center max-w-xs">{etiqueta}</div>
    </div>
  );
}

// Rueda del ratón y flechas: el mismo gesto que la perilla física.
function useTeclado({ girar, apretar, onMantener }) {
  useEffect(() => {
    let desde = null;
    function tecla(e) {
      if (e.key === 'ArrowUp') { e.preventDefault(); girar(1); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); girar(-1); }
      else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (desde === null) desde = Date.now();
      }
    }
    function suelta(e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const rato = desde === null ? 0 : Date.now() - desde;
      desde = null;
      if (rato >= 2000 && onMantener) onMantener();
      else apretar();
    }
    function rueda(e) { girar(e.deltaY > 0 ? -1 : 1); }
    window.addEventListener('keydown', tecla);
    window.addEventListener('keyup', suelta);
    window.addEventListener('wheel', rueda, { passive: true });
    return () => {
      window.removeEventListener('keydown', tecla);
      window.removeEventListener('keyup', suelta);
      window.removeEventListener('wheel', rueda);
    };
  }, [girar, apretar, onMantener]);
}

export { ESTADOS };

// ELEGIR EL MOTIVO, girando.
//
// UNO A LA VEZ Y GRANDE, no una lista de seis en letra pequeña: quien
// mira esta pantalla lleva guantes, está de pie y tiene prisa. El que
// está elegido ocupa la pantalla; los de al lado se insinúan.
// =====================================================================
// EL MENÚ DEL SUPERVISOR · TC-31 (§A3)
// =====================================================================
// Las cuatro cosas que pide el anexo, y dos de ellas ni siquiera van al
// servidor: «pendientes sin subir» y «forzar sincronización» son la cola
// de este aparato y se resuelven aquí. Las otras dos —último latido de
// las demás y cerrar sus sesiones— vienen de `/supervisor`.
//
// LO QUE ESTE MENÚ NO HACE: mover inventario. Un supervisor que quiere
// ajustar algo lo ajusta con la perilla como todo el mundo, y queda a su
// nombre. Este menú es para mirar y para cerrar.
export function MenuSupervisor({ datos, pendientes, onSincronizar, onCerrar, onSalir }) {
  return (
    <div className="text-center space-y-3 max-w-md w-full">
      <div className="text-sm opacity-70">Menú de supervisor</div>

      {/* LO SUYO PRIMERO: la cola de esta perilla, que es lo único que
          esta persona puede arreglar ahora mismo sin moverse. */}
      <div className="rounded-lg bg-white/5 p-3 space-y-2">
        <div className="text-lg font-semibold">
          {pendientes === 0
            ? 'Esta terminal no tiene nada sin subir'
            : `${pendientes} giro${pendientes === 1 ? '' : 's'} sin subir`}
        </div>
        <button
          type="button"
          onClick={onSincronizar}
          disabled={pendientes === 0}
          className="bg-sky-700 disabled:bg-gray-700 text-white rounded-lg
                     px-4 py-2 text-sm font-semibold"
        >
          Subirlos ahora
        </button>
        {/* UN BOTÓN APAGADO DICE POR QUÉ. */}
        {pendientes === 0 && (
          <p className="text-xs opacity-50">
            No hay nada que subir ni que forzar.
          </p>
        )}
      </div>

      <div className="text-sm opacity-70">Las otras terminales de aquí</div>
      {(datos?.terminales || []).filter((t) => !t.soy_yo).length === 0 && (
        <p className="text-sm opacity-60">
          Ésta es la única terminal de esta sucursal.
        </p>
      )}
      {(datos?.terminales || []).filter((t) => !t.soy_yo).map((t) => {
        const estado = comoEstaLaTerminal(t);
        return (
          <div key={t.id_dispositivo} className="rounded-lg bg-white/5 p-3 text-left space-y-1">
            <div className="font-semibold">{t.nombre}</div>
            <div className={`text-xs ${
              estado.color === 'rojo' ? 'text-red-300'
                : estado.color === 'ambar' ? 'text-amber-300'
                  : estado.color === 'verde' ? 'text-green-300' : 'opacity-50'
            }`}>
              {estado.frase}
            </div>
            {t.operador ? (
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-sm">{t.operador} está trabajando ahí</span>
                <button
                  type="button"
                  onClick={() => onCerrar(t)}
                  className="bg-red-700 text-white rounded px-3 py-1.5 text-xs font-semibold shrink-0"
                >
                  Cerrar su sesión
                </button>
              </div>
            ) : (
              <div className="text-sm opacity-60">Nadie vinculado</div>
            )}
          </div>
        );
      })}

      <button type="button" onClick={onSalir} className="text-sm underline opacity-70">
        Volver a la repisa
      </button>
    </div>
  );
}


export function ElegirLote({ lotes, indice, sku, delta }) {
  const actual = lotes[indice];
  const sugerido = indice === 0;
  const retenido = actual?.estado_calidad
    && actual.estado_calidad !== 'LIBERADO';
  // EL VERBO CAMBIA CON EL SIGNO, y no es cosmético: la prueba del
  // cliente cazó que el consejo decía «si sacaste de otro» a alguien que
  // estaba METIENDO material. Una frase que describe lo contrario de lo
  // que la persona acaba de hacer es peor que no decir nada, porque le
  // hace dudar de si entendió mal la pantalla anterior.
  const verbo = delta < 0 ? 'sacaste' : 'metiste';
  return (
    <div className="text-center space-y-3 max-w-md">
      <div className="text-sm opacity-70">
        ¿De qué lote {verbo} {Math.abs(delta)} de {sku}?
      </div>
      <h1 className={`text-4xl font-bold ${retenido ? 'text-amber-300' : 'text-sky-300'}`}>
        {actual ? actual.codigo_lote : 'Sin lotes'}
      </h1>
      <div className="text-sm opacity-70">{etiquetaDeLote(actual, sugerido)}</div>
      {/* SIN LOTES NO SE CUENTA HASTA UNO. «1 de 0» es la clase de
          frase que hace dudar de todo lo demás que dice la pantalla. */}
      {lotes.length > 0 && (
        <div className="text-xs opacity-40">
          {indice + 1} de {lotes.length} · gira para cambiar
        </div>
      )}
      {/* EL PRIMERO DICE POR QUÉ ES EL PRIMERO. «Sugerido» a secas
          obliga a confiar; decir que es el que caduca antes explica el
          FIFO sin mandar a nadie a un manual. */}
      {sugerido && actual && (
        <p className="text-xs opacity-50">
          Éste es el que TEGO sugiere: es el que <strong>caduca o llegó
          antes</strong>. Si {verbo} de otro, gira y elígelo — lo que
          importa es que quede apuntado el de verdad.
        </p>
      )}
      {/* LA TERMINAL NO JUZGA, PERO TAMPOCO CALLA. Quien decide si un
          lote retenido puede salir es calidad; elegirlo a ciegas es cómo
          se manda material retenido a la línea. */}
      {retenido && (
        <p className="text-xs text-amber-300">
          Calidad tiene este lote <strong>retenido</strong>. Puedes
          apuntarlo si de ahí lo sacaste, pero avísale a tu encargado
          antes de usar más piezas.
        </p>
      )}
    </div>
  );
}


export function ElegirMotivo({ motivos, indice, producto, mios }) {
  const actual = motivos[indice];
  return (
    <div className="text-center space-y-3 max-w-md">
      <div className="text-sm opacity-70">
        ¿Por qué está mal{producto ? ` ${producto.nombre}` : ''}?
      </div>
      <h1 className="text-4xl font-bold text-red-300">
        {actual ? actual.nombre : 'Sin motivos'}
      </h1>
      <div className="text-xs opacity-40">
        {indice + 1} de {motivos.length} · gira para cambiar
      </div>
      {/* SUS NÚMEROS, NO LOS DE NADIE MÁS (§B11). */}
      {mios > 0 && (
        <div className="text-sm opacity-70">
          Llevas {mios} apuntada{mios === 1 ? '' : 's'} hoy.
        </div>
      )}
      <p className="text-xs opacity-50">
        Apuntar una pieza mala <strong>no la descuenta</strong> del
        inventario: sigue en la repisa hasta que alguien decida qué hacer
        con ella.
      </p>
    </div>
  );
}
